import React, { useState, useRef, useEffect, useContext } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { ThemeContext } from '../App';
import { debug } from '../utils/debug';
import { ensureComponentInViewport } from '../utils/detachableHelpers';

const DetachableCalculator = ({ onClose }) => {
  const {
    mode,
    weatherCondition,
    weatherThemeEnabled,
    weatherThemeMode,
    selectedWeatherTheme,
    solidDetachableComponents
  } = useContext(ThemeContext);

  const isDarkMode = mode === 'dark';
  const theme = useTheme();
  
  // Position on the right side by default, with localStorage support
  const [position, setPosition] = useState(() => {
    // Get position and ensure it's within viewport
    return ensureComponentInViewport(
      'expenseCalculatorPosition',
      { x: window.innerWidth - 350, y: 80 },
      280,
      400
    );
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const calculatorRef = useRef(null);
  const displayRef = useRef(null);

  // Calculator state
  const [display, setDisplay] = useState('0');
  const [secondaryDisplay, setSecondaryDisplay] = useState('0');  // For memory or calculations
  const [hasFocus, setHasFocus] = useState(false);
  const [shouldResetDisplay, setShouldResetDisplay] = useState(false);
  const [waitingForSecondOperand, setWaitingForSecondOperand] = useState(false);

  // Add window resize handler to keep component in viewport
  useEffect(() => {
    const handleResize = () => {
      // Only update position if not currently dragging
      if (!isDragging) {
        const newPosition = ensureComponentInViewport(
          'expenseCalculatorPosition',
          { x: window.innerWidth - 350, y: 80 },
          280,
          400,
          true // Maintain right distance
        );
        setPosition(newPosition);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDragging]);

  // Get the effective weather condition based on theme mode
  const effectiveWeatherCondition = weatherThemeEnabled
    ? (weatherThemeMode === 'manual' ? selectedWeatherTheme : weatherCondition)
    : null;
    
  debug('calculator', 'Weather condition', effectiveWeatherCondition);
  debug('calculator', 'Weather theme enabled', weatherThemeEnabled);

  // Get accent color based on weather condition
  const getAccentColor = (condition, isDark) => {
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ffa000' : '#ff9800'; // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#78909c' : '#607d8b'; // Blue-gray for clouds
      case 'rain':
      case 'drizzle':
        return isDark ? '#26c6da' : '#00acc1'; // Cyan/teal for rain
      case 'snow':
        return isDark ? '#90caf9' : '#42a5f5'; // Blue for snow
      case 'thunderstorm':
        return isDark ? '#9575cd' : '#5e35b1'; // Purple for thunderstorm
      case 'spring':
        return isDark ? '#66bb6a' : '#81c784'; // Green for spring
      case 'summer':
        return isDark ? '#26c6da' : '#00bcd4'; // Cyan for summer
      case 'autumn':
        return isDark ? '#ff7043' : '#ff5722'; // Deep orange for autumn
      case 'winter':
        return isDark ? '#90caf9' : '#2196f3'; // Blue for winter
      default:
        return isDark ? '#bb86fc' : '#1976d2';
    }
  };
  
  // Weather icon to display as a decorative element
  const getWeatherIcon = () => {
    if (!effectiveWeatherCondition) return '';
    
    switch(effectiveWeatherCondition.toLowerCase()) {
      case 'clear': return '☀️';
      case 'clouds': return '☁️';
      case 'rain': case 'drizzle': return '🌧️';
      case 'snow': return '❄️';
      case 'thunderstorm': return '⚡';
      case 'spring': return '🌱';
      case 'summer': return '🏖️';
      case 'autumn': return '🍂';
      case 'winter': return '☃️';
      default: return '';
    }
  };

  // Define color scheme based on weather theme status
  // Final theme colors sourced from the active palette so the widget mirrors the page
  const pageBackgroundColor = theme.palette?.background?.default || (isDarkMode ? '#121212' : '#f5f5f5');

  const bgColor = pageBackgroundColor;

  debug('calculator', 'Weather condition', effectiveWeatherCondition);
  debug('calculator', 'Weather theme enabled', weatherThemeEnabled);
  debug('calculator', 'Background color', bgColor);

  const textColor = theme.palette?.text?.primary || (isDarkMode ? '#ffffff' : '#212529');
  const secondaryTextColor = theme.palette?.text?.secondary || (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)');

  const defaultDisplayBgColor = theme.palette?.background?.paper || (isDarkMode ? '#121212' : '#f8f9fa');
  const defaultButtonBgColor = isDarkMode ? '#333333' : '#e9ecef';
  const defaultClearButtonBgColor = isDarkMode ? '#666666' : '#dee2e6';

  // Apply transparency based on setting
  const getTransparentColor = (color, opacity) => {
    if (solidDetachableComponents) {
      return color;
    }
    return `rgba(${color}, ${opacity})`;
  };
  
  const displayBgColor = weatherThemeEnabled && effectiveWeatherCondition 
    ? (isDarkMode ? (solidDetachableComponents ? '#121212' : 'rgba(0, 0, 0, 0.4)') 
                  : (solidDetachableComponents ? '#f8f9fa' : 'rgba(255, 255, 255, 0.7)')) 
    : defaultDisplayBgColor;
    
  const buttonBgColor = weatherThemeEnabled && effectiveWeatherCondition
    ? (isDarkMode ? (solidDetachableComponents ? '#333333' : 'rgba(0, 0, 0, 0.3)') 
                  : (solidDetachableComponents ? '#e9ecef' : 'rgba(255, 255, 255, 0.6)')) 
    : defaultButtonBgColor;
    
  const clearButtonBgColor = weatherThemeEnabled && effectiveWeatherCondition
    ? (isDarkMode ? (solidDetachableComponents ? '#555555' : 'rgba(0, 0, 0, 0.5)') 
                  : (solidDetachableComponents ? '#dee2e6' : 'rgba(255, 255, 255, 0.4)')) 
    : defaultClearButtonBgColor;
    
  // Final theme colors (with weather theme priority)
  const accentColor = weatherThemeEnabled && effectiveWeatherCondition 
    ? getAccentColor(effectiveWeatherCondition, isDarkMode)
    : (isDarkMode ? '#bb86fc' : '#1976d2');
    
  // Border color for the calculator
  const borderColor = weatherThemeEnabled && effectiveWeatherCondition
    ? `${accentColor}66` // Add transparency to the accent color
    : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');

  // Handle mouse down event to start dragging
  const handleMouseDown = (e) => {
    // Prevent event from bubbling up to avoid duplicate events
    e.stopPropagation();
    
    if (isDragging) return; // Don't start a new drag if already dragging
    
    if (e.target.closest('button')) return; // Don't start drag when clicking buttons
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle mouse move event when dragging.
  // Coalesced into at most one setPosition (and therefore one re-render)
  // per animation frame via requestAnimationFrame, instead of one per
  // mousemove event - mousemove can fire at a much higher rate than the
  // display can even show, so this was re-rendering the whole widget far
  // more often than any of it was ever visible for. Same fix as
  // DetachableClock.js.
  const dragPositionRef = useRef(null);
  const dragRafRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    // Prevent default to avoid text selection during drag
    e.preventDefault();

    dragPositionRef.current = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        setPosition(dragPositionRef.current);
      });
    }
  };

  // Handle mouse up event to stop dragging
  const handleMouseUp = (e) => {
    // Cancel any still-pending throttled move update - it holds a stale
    // position that would otherwise land after (and overwrite) the final
    // adjusted one set below.
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }

    if (isDragging) {
      // Calculate raw position first
      const rawPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };

      // Ensure the position is within the viewport bounds
      const adjustedPosition = ensureComponentInViewport(
        'expenseCalculatorPosition',
        rawPosition,
        280,
        400
      );

      // Update state with the adjusted position
      setPosition(adjustedPosition);
    }
    setIsDragging(false);
  };

  // Focus and blur handlers
  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    setHasFocus(false);
  };

  // Set up event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
  }, [isDragging, dragStart, position]);

  // Save position to localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.setItem('expenseCalculatorPosition', JSON.stringify(position));
    };
  }, [position]);

  // Auto-focus calculator when it appears
  useEffect(() => {
    // Focus the calculator element when it mounts
    if (calculatorRef.current) {
      calculatorRef.current.focus();
      setHasFocus(true);
    }
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only process key events when calculator is focused
      if (!hasFocus) return;
      
      // Prevent default behaviors for calculator keys
      if (!/^F\d+$/.test(e.key) && e.key !== 'Tab') {
        e.preventDefault();
      }
      
      // Map keyboard keys to calculator functions
      switch (e.key) {
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          inputDigit(e.key);
          break;
        case '.':
          inputDecimal();
          break;
        case '+':
          handleOperator('+');
          break;
        case '-':
          handleOperator('-');
          break;
        case '*':
        case 'x':
        case '×':
          handleOperator('*');
          break;
        case '/':
        case '÷':
          handleOperator('/');
          break;
        case '(':
          inputParenthesis('(');
          break;
        case ')':
          inputParenthesis(')');
          break;
        case 'Enter':
        case '=':
          performCalculation();
          break;
        case 'Backspace':
          handleBackspace();
          break;
        case 'Escape':
        case 'c':
        case 'C':
          clearDisplay();
          break;
        default:
          // Ignore other keys
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasFocus, display, shouldResetDisplay, waitingForSecondOperand]);

  // Calculator functions
  const inputDigit = (digit) => {
    if (shouldResetDisplay) {
      setDisplay(digit);
      setShouldResetDisplay(false);
    } else if (waitingForSecondOperand) {
      setDisplay(display + digit);
      setWaitingForSecondOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (shouldResetDisplay) {
      setDisplay('0.');
      setShouldResetDisplay(false);
      return;
    }
    
    if (waitingForSecondOperand) {
      setDisplay(display + '0.');
      setWaitingForSecondOperand(false);
      return;
    }

    // Check if the current number being entered already has a decimal point
    const parts = display.split(/[+\-*/]/);
    const currentNumber = parts[parts.length - 1];
    
    if (!currentNumber.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const inputParenthesis = (parenthesis) => {
    if (shouldResetDisplay) {
      setDisplay(parenthesis);
      setShouldResetDisplay(false);
      return;
    }
    
    if (waitingForSecondOperand) {
      setDisplay(display + parenthesis);
      setWaitingForSecondOperand(false);
    } else {
      if (display === '0') {
        setDisplay(parenthesis);
      } else {
        setDisplay(display + parenthesis);
      }
    }
  };

  const handleBackspace = () => {
    if (shouldResetDisplay || waitingForSecondOperand) {
      return;
    }
    
    if (display !== '0') {
      if (display.length === 1) {
        setDisplay('0');
      } else {
        setDisplay(display.slice(0, -1));
      }
    }
  };

  const clearDisplay = () => {
    setDisplay('0');
    setSecondaryDisplay('0');
    setWaitingForSecondOperand(false);
    setShouldResetDisplay(false);
  };

  const handleOperator = (nextOperator) => {
    // If we already have a result and are starting a new calculation
    if (shouldResetDisplay) {
      setDisplay(display + nextOperator);
      setShouldResetDisplay(false);
    } else {
      // Add the operator to the equation
      setDisplay(display + nextOperator);
    }
    
    setWaitingForSecondOperand(false);
  };

  const safeEval = (expression) => {
    try {
      // Replace all instances of 'x' with '*' for multiplication
      expression = expression.replace(/×/g, '*');
      expression = expression.replace(/÷/g, '/');
      
      // Convert the expression to ensure all numbers are treated as numbers
      // This prevents string concatenation issues (especially with + operator)
      
      // First ensure all decimal numbers are properly handled
      // Adding space around operators to make next step easier
      expression = expression.replace(/([+\-*/()])/g, ' $1 ');
      
      // Split by spaces and process each token
      const tokens = expression.split(/\s+/).filter(Boolean);
      const processedTokens = tokens.map(token => {
        // If it's a number, wrap it in Number() conversion
        if (/^(\d+\.?\d*|\.\d+)$/.test(token)) {
          return `Number("${token}")`;
        }
        return token;
      });
      
      // Reassemble the expression
      expression = processedTokens.join(' ');
      
      // Use Function constructor to evaluate the expression
      // This is safer than eval()
      return new Function('return ' + expression)();
    } catch (e) {
      console.error('Error evaluating expression:', e);
      return 'Error';
    }
  };

  const performCalculation = () => {
    if (display === '0') {
      return;
    }
    
    try {
      // Keep the formula as is, only update the secondary display with the result
      const result = safeEval(display);
      
      if (isNaN(result) || !isFinite(result)) {
        setSecondaryDisplay('Error');
      } else {
        // Format the result - if it's an integer, don't show decimal places
        const formattedResult = Number.isInteger(result) ? result.toString() : result.toFixed(8).replace(/\.?0+$/, '');
        setSecondaryDisplay(formattedResult);
      }
      
      // Don't reset the display - we want to keep the formula
      setShouldResetDisplay(false);
    } catch (error) {
      setSecondaryDisplay('Error');
    }
  };

  // Button style
  const buttonStyle = {
    height: '40px',
    minWidth: '40px',
    fontSize: '1rem',
    color: textColor,
    backgroundColor: buttonBgColor,
    borderRadius: '4px',
    border: 'none',
    padding: 0,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    },
  };

  // Special button styles
  const clearButtonStyle = {
    ...buttonStyle,
    backgroundColor: clearButtonBgColor,
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
    },
  };

  const equalsButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#0d6efd',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#2196f3',
    },
  };

  return (
    <Paper
      ref={calculatorRef}
      sx={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        zIndex: 1050,
        padding: '12px',
        background: bgColor,
        borderRadius: '12px',
        boxShadow: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        width: '280px', // Match width with calendar and clock
        color: textColor,
        transition: 'background 0.5s ease, transform 0.2s ease, color 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)'
        },
        border: `1px solid ${borderColor}`
      }}
      onMouseDown={handleMouseDown}
      tabIndex="-1"
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" sx={{ 
          fontWeight: 'medium',
          display: 'flex',
          alignItems: 'center' 
        }}>
          {weatherThemeEnabled && effectiveWeatherCondition && getWeatherIcon()}
          <span style={{ marginLeft: weatherThemeEnabled && effectiveWeatherCondition ? '4px' : 0 }}>
            Calculator
          </span>
        </Typography>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ 
            color: secondaryTextColor,
            padding: '4px',
            '&:hover': { color: textColor }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* Formula line - smaller font size */}
      <Box 
        sx={{
          bgcolor: displayBgColor, // Removed transparency
          borderRadius: '4px',
          padding: '2px 12px', // Further reduced from 4px to 2px
          textAlign: 'right',
          fontSize: '1rem', // Reduced from 1.1rem to 1rem
          fontFamily: 'monospace',
          minHeight: '22px', // Further reduced from 30px to 22px
          mb: 1,
          color: textColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {display}
      </Box>
      
      {/* Result line - larger font size */}
      <Box 
        ref={displayRef}
        sx={{
          bgcolor: displayBgColor,
          borderRadius: '4px',
          padding: '8px 12px',
          textAlign: 'right',
          fontSize: '1.7rem',
          fontWeight: 'bold',
          fontFamily: 'monospace',
          minHeight: '56px',
          mb: 1.5,
          color: textColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          border: hasFocus ? `1px solid ${accentColor}` : 'none',
          boxShadow: hasFocus ? `0 0 0 1px ${accentColor}33` : 'none',
          transition: 'all 0.2s ease'
        }}
      >
        {secondaryDisplay}
      </Box>
      
      {/* Calculator buttons */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
        {/* Row 1 */}
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.1rem',
            bgcolor: clearButtonBgColor,
            color: textColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={clearDisplay}
        >
          C
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.1rem',
            bgcolor: buttonBgColor,
            color: textColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputParenthesis('(')}
        >
          (
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.1rem',
            bgcolor: buttonBgColor,
            color: textColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputParenthesis(')')}
        >
          )
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.1rem',
            bgcolor: buttonBgColor,
            color: textColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={handleBackspace}
        >
          ⌫
        </Button>

        {/* Row 2 */}
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('7')}
        >
          7
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('8')}
        >
          8
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('9')}
        >
          9
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: accentColor,
            fontWeight: 'medium',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (solidDetachableComponents 
                    ? `${accentColor}22` 
                    : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc'),
              boxShadow: (weatherThemeEnabled && effectiveWeatherCondition && !solidDetachableComponents) 
                ? `0 0 8px ${accentColor}66` 
                : 'none'
            },
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => handleOperator('/')}
        >
          ÷
        </Button>
      
        {/* Row 3 */}
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('4')}
        >
          4
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('5')}
        >
          5
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('6')}
        >
          6
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: accentColor,
            fontWeight: 'medium',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (solidDetachableComponents 
                    ? `${accentColor}22` 
                    : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc'),
              boxShadow: (weatherThemeEnabled && effectiveWeatherCondition && !solidDetachableComponents) 
                ? `0 0 8px ${accentColor}66` 
                : 'none'
            },
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => handleOperator('*')}
        >
          ×
        </Button>
      
        {/* Row 4 */}
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('1')}
        >
          1
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('2')}
        >
          2
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => inputDigit('3')}
        >
          3
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: accentColor,
            fontWeight: 'medium',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (solidDetachableComponents 
                    ? `${accentColor}22` 
                    : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc'),
              boxShadow: (weatherThemeEnabled && effectiveWeatherCondition && !solidDetachableComponents) 
                ? `0 0 8px ${accentColor}66` 
                : 'none'
            },
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => handleOperator('-')}
        >
          −
        </Button>
      
        {/* Row 5 */}
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }}
          onClick={() => inputDigit('0')}
        >
          0
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={inputDecimal}
        >
          .
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: accentColor,
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (solidDetachableComponents 
                    ? `${accentColor}22` 
                    : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc'),
              boxShadow: (weatherThemeEnabled && effectiveWeatherCondition && !solidDetachableComponents) 
                ? `0 0 8px ${accentColor}66` 
                : 'none'
            },
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={() => handleOperator('+')}
        >
          +
        </Button>
        <Button 
          sx={{ 
            minWidth: 0, 
            fontSize: '1.2rem',
            bgcolor: buttonBgColor,
            color: textColor,
            fontWeight: 'normal',
            '&:hover': { 
              bgcolor: (weatherThemeEnabled && effectiveWeatherCondition)
                ? (isDarkMode 
                    ? (solidDetachableComponents ? '#444444' : 'rgba(255, 255, 255, 0.1)') 
                    : (solidDetachableComponents ? '#c6c9cc' : 'rgba(0, 0, 0, 0.1)'))
                : (isDarkMode ? '#555555' : '#c6c9cc')
            },
            borderRadius: '4px',
            transition: 'background-color 0.3s ease, transform 0.1s ease',
            '&:active': {
              transform: 'scale(0.97)'
            }
          }} 
          onClick={performCalculation}
        >
          =
        </Button>
      </Box>
    </Paper>
  );
};

export default DetachableCalculator; 
